import __var_0__ from '@stdlib/math/base/assert/is-nan';
import __var_1__ from '@stdlib/math/base/assert/is-infinite';
import __var_2__ from '@stdlib/math/base/special/pow';
import __var_3__ from '@stdlib/math/base/special/floor';
import __var_4__ from '@stdlib/math/base/special/ceil';
import __var_5__ from '@stdlib/math/base/special/log2';
import __var_6__ from '@stdlib/constants/math/float64-max-base2-exponent';
import __var_7__ from '@stdlib/constants/math/float64-min-base2-exponent-subnormal';
import __var_8__ from '@stdlib/constants/math/float64-pinf';
/**

*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

'use strict';

// MODULES //
var isnan = __var_0__;
var isInfinite = __var_1__;
var pow = __var_2__;
var floor = __var_3__;
var ceil = __var_4__;
var log2 = __var_5__;
var MAX_EXP = __var_6__;
var MIN_EXP_SUBNORMAL = __var_7__;
var PINF = __var_8__;


// VARIABLES //

// 2^1023:
var HUGE = pow( 2.0, MAX_EXP );
var HALF_HUGE = HUGE / 2.0;


// MAIN //

/**
* Rounds a numeric value to the nearest power of two on a linear scale.
*
* @param {number} x - input value
* @returns {number} rounded value
*
* @example
* var v = round2( 3.141592653589793 );
* // returns 4.0
*
* @example
* var v = round2( 13.0 );
* // returns 16.0
*
* @example
* var v = round2( -0.314 );
* // returns -0.25
*/
function round2( x ) {
	var sign;
	var half;
	var p1;
	var p2;
	var y1;
	var y2;
	var p;
	if (
		isnan( x ) ||
		isInfinite( x ) ||
		x === 0.0
	) {
		return x;
	}
	if ( x < 0 ) {
		x = -x;
		sign = -1.0;
	} else {
		sign = 1.0;
	}
	// Solve the equation `2^p = x` for `p`:
	p = log2( x );

	// If provided the smallest subnormal, no rounding possible:
	if ( p === MIN_EXP_SUBNORMAL ) {
		return x;
	}
	// Find the previous and next integer powers:
	p1 = floor( p );
	p2 = ceil( p );

	// Handle overflow:
	if ( p1 === MAX_EXP ) {
		if ( x - HUGE >= HALF_HUGE ) {
			return sign * PINF; // sign-preserving
		}
		return sign * HUGE;
	}
	// Compute previous and next powers of two:
	y1 = pow( 2.0, p1 );
	y2 = pow( 2.0, p2 );

	// Find the closest power of two:
	half = ( y2 - y1 ) / 2.0;
	if ( y1+half > x ) {
		return sign * y1;
	}
	return sign * y2;
}


// EXPORTS //
export default round2;
